import type { ItemRowsQuery } from './graphql/__generated__/ItemRowsQuery.interface';
import { ItemRowsDocument } from './graphql/__generated__/ItemRowsQuery.interface';
import { query } from 'owa-apollo';
import type { SessionDataWithGraphQL, PagingInfoInput, ResolverContext } from 'owa-graph-schema';
import type { MailboxInfo } from 'owa-client-types';
import { mapFindItemResponseMessageToGql } from 'owa-mail-item-row-gql-mappers';
import type { SortBy } from 'owa-mail-list-store';
import {
    getGqlSortColumnFromOwsSortColumn,
    getGqlViewFilterFromOwsViewFilter,
    getGqlFocusedViewFilterFromOwsFocusedViewFilter,
} from 'owa-mail-list-store';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import type ViewFilter from 'owa-service/lib/contract/ViewFilter';
import type RequestOptions from 'owa-service/lib/RequestOptions';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { extractFindMailFolderItemResponse } from './utils/extractFindMailFolderItemResponse';
import type { PerformanceDatapoint } from 'owa-analytics';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { addTimingsToGQLQuery } from 'owa-analytics-shared';

type ItemRowConnection = ItemRowsQuery['itemRows'];

/**
 * Loads item rows for list view when in Message view
 * @param folderId the folder id
 * @param startIndex the startIndex to load the table
 * @param paging the paging type
 * @param viewFilter the view filter
 * @param sortBy the sort by properties
 * @param canFolderSortByRenewTime can the folder be sorted by renew time
 * @param focusedViewFilter the focused view filter
 * @param findItemShapeName - the shape for the request
 * @param initialSessionData The initial session data which is only present during the initial bootstrap
 * @param options - optional parameters to be passed to request (method and/or headers)
 * @param categoryName - the category name
 * @param searchFolderId the search folder id
 * @returns a promise of type FindItemResponseMessage
 */
export async function getItemRows(
    folderId: string,
    viewFilter: ViewFilter,
    sortBy: SortBy,
    canFolderSortByRenewTime: boolean,
    focusedViewFilter: FocusedViewFilter,
    findItemShapeName: string,
    mailboxInfoInput: MailboxInfo,
    pagingInfoInput: PagingInfoInput,
    initialSessionData: SessionDataWithGraphQL | undefined,
    options: RequestOptions,
    categoryName?: string,
    shouldIgnoreSessionData?: boolean,
    searchFolderId?: string,
    isTablePrefetched?: boolean,
    isReload?: boolean
): Promise<ItemRowConnection> {
    const lastSwitchMailFolderDp = returnTopExecutingActionDatapoint((dp: PerformanceDatapoint) => {
        return dp.getEventName() == 'SwitchMailFolder';
    });

    // If using filterSortBy override, then ignore the session data as the sortby is different.
    if (
        initialSessionData &&
        folderId === folderNameToId('inbox', mailboxInfoInput) &&
        !shouldIgnoreSessionData
    ) {
        lastSwitchMailFolderDp?.addCheckpoint('SMF_GIR_Inbx');
        const sessionDataFindMailFolderItemResponse =
            extractFindMailFolderItemResponse(initialSessionData);
        if (sessionDataFindMailFolderItemResponse) {
            // Return the session data response if available instead of going to the server
            return Promise.resolve(
                mapFindItemResponseMessageToGql(
                    sessionDataFindMailFolderItemResponse,
                    mailboxInfoInput
                )
            );
        } else if (initialSessionData.itemRows) {
            // Offline session data provides rows in graphql schema
            return Promise.resolve(initialSessionData.itemRows);
        }
    }

    lastSwitchMailFolderDp?.addCheckpoint('SMF_GIR_GAC');
    const isOutbox: boolean = folderIdToName(folderId) === 'outbox';
    const resolverPolicy = isOutbox ? 'localOnly' : isReload ? 'serverOnly' : undefined;
    const result = await addTimingsToGQLQuery(
        lastSwitchMailFolderDp,
        {
            isEnabled: true,
            discardIfDefined: true,
        },
        wfContext =>
            query(ItemRowsDocument, {
                variables: {
                    folderId,
                    pagingInfo: pagingInfoInput,
                    searchFolderId,
                    viewFilter: getGqlViewFilterFromOwsViewFilter(viewFilter),
                    focusedViewFilter:
                        getGqlFocusedViewFilterFromOwsFocusedViewFilter(focusedViewFilter),
                    sortBy: {
                        sortColumn: getGqlSortColumnFromOwsSortColumn(sortBy.sortColumn),
                        sortDirection: sortBy.sortDirection,
                        canSortByRenewTime: canFolderSortByRenewTime,
                        isDraftsFolder: folderIdToName(folderId) === 'drafts',
                    },
                    category: categoryName,
                    mailboxInfo: mailboxInfoInput,
                    shapeName: findItemShapeName,
                    isScheduledFolder: folderIdToName(folderId) === 'scheduled',
                },
                context: {
                    requestOptions: options,
                    throttleOperation: isTablePrefetched,
                    resolverPolicy,
                    queryDeduplication: false, // Avoid waiting for a throttled prefetch
                    ...wfContext,
                } as ResolverContext,
            })
    );

    lastSwitchMailFolderDp?.addCheckpoint('SMF_GIR_APQ');
    return result.data.itemRows;
}
